import { BREATHE_CLASS } from "@neucleos/motion-primitives";
import type { SVGProps } from "react";

import type { SessionFace } from "~/neucleos/sessionFace";

/**
 * The Neucleos triangle — the design of record's brand glyph (the 24-grid
 * triangle beside the wordmark), in the Neucleos identity blue by default
 * (`text-neucleos-identity`). The principal's word 2026-09-02: "change the
 * icon for a session, from the Cleo mark, and use the blue Neucleos
 * triangle" — a session's face is the World's system, not Cleo's seal.
 * Pure geometry, never clickable; decorative by default because every
 * wearer names the session beside it.
 *
 * FD-CAN-39 (the principal, the same day: "use the Neucleos blue triangle
 * icons in the sessions, as an indicator"): a wearer may hand the glyph the
 * session's FACE — `data-session-face` is the attribute the one stylesheet
 * rule keys the three intensities on (dormant silver, waiting lit, alive
 * lit), and the alive face alone mounts the motion-primitives breath.
 * Motion is information: the class exists exactly while the agent runs.
 */
export const NEUCLEOS_TRIANGLE_PATH = "M12 3.2 22 20.8H2Z";

export function NeucleosTriangle({
  decorative = true,
  label = "Neucleos",
  face,
  className,
  ...svgProps
}: Omit<SVGProps<SVGSVGElement>, "color"> & {
  readonly decorative?: boolean;
  readonly label?: string;
  /** FD-CAN-39: the session state the glyph wears; absent = the plain brand glyph. */
  readonly face?: SessionFace;
}) {
  const classes =
    face === "alive"
      ? className === undefined
        ? BREATHE_CLASS
        : `${className} ${BREATHE_CLASS}`
      : className;
  return (
    <svg
      {...svgProps}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : label}
      className={classes}
      data-neucleos-triangle=""
      data-session-face={face}
      fill="currentColor"
      focusable="false"
      role={decorative ? undefined : "img"}
      viewBox="0 0 24 24"
    >
      <path d={NEUCLEOS_TRIANGLE_PATH} />
    </svg>
  );
}
